/**
 * Allowlist por distribuidor: el admin marca qué SKUs se importan.
 * El listado va paginado; la selección se guarda entera, no solo la página.
 */

import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResourceIndex, type ResourceIndexColumn } from '@/components/shared/ResourceIndex';
import { selectPageIds, toggleSelectedId } from '@/components/shared/resourceIndexState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { esCount } from '@/lib/es-count';
import {
    useCatalogAllowlist,
    useLoadCatalogRemoteList,
    useReplaceCatalogAllowlist,
} from './hooks/useStoreCatalog';
import type { CatalogRemoteItem } from './types';

interface CatalogAllowlistPanelProps {
    supplier: string;
    onMessage: (msg: { type: 'ok' | 'err'; text: string }) => void;
}

const PAGE_SIZE = 50;
type AllowView = 'all' | 'selected' | 'imported' | 'pending';

function toKeySet(ids: string[]): Set<string> {
    return new Set(ids.filter(Boolean));
}

export function CatalogAllowlistPanel({ supplier, onMessage }: CatalogAllowlistPanelProps) {
    const savedQ = useCatalogAllowlist(supplier);
    const remoteMut = useLoadCatalogRemoteList();
    const saveMut = useReplaceCatalogAllowlist();
    const [search, setSearch] = useState('');
    const [view, setView] = useState<AllowView>('all');
    const [page, setPage] = useState(1);
    const [remoteItems, setRemoteItems] = useState<CatalogRemoteItem[] | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [seenSupplier, setSeenSupplier] = useState(supplier);
    const savedKey = (savedQ.data ?? []).map((row) => row.externalId).join('|');
    const [loadedSavedKey, setLoadedSavedKey] = useState('');

    if (seenSupplier !== supplier) {
        setSeenSupplier(supplier);
        setRemoteItems(null);
        setSearch('');
        setView('all');
        setPage(1);
        setSelected(new Set());
        setLoadedSavedKey('');
    } else if (!remoteItems && savedQ.data && loadedSavedKey !== savedKey) {
        setLoadedSavedKey(savedKey);
        setSelected(toKeySet(savedQ.data.map((row) => row.externalId)));
    }

    const rows = useMemo(() => {
        if (remoteItems) return remoteItems;
        return (savedQ.data ?? []).map((row) => ({
            externalId: row.externalId,
            nombre: row.nombre || row.externalId,
            sourceUrl: row.sourceUrl || '',
            selected: selected.has(row.externalId),
            imported: row.imported,
        }));
    }, [remoteItems, savedQ.data, selected]);

    const metaById = useMemo(() => {
        const map = new Map<string, { nombre: string; sourceUrl: string }>();
        for (const row of rows) map.set(row.externalId, { nombre: row.nombre, sourceUrl: row.sourceUrl });
        return map;
    }, [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (view === 'selected' && !selected.has(row.externalId)) return false;
            if (view === 'imported' && !row.imported) return false;
            if (view === 'pending' && (row.imported || !selected.has(row.externalId))) return false;
            if (!q) return true;
            return `${row.nombre} ${row.externalId} ${row.sourceUrl}`.toLowerCase().includes(q);
        });
    }, [rows, search, view, selected]);

    const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const selectedIds = [...selected];

    const onLoadRemote = async () => {
        try {
            const res = await remoteMut.mutateAsync(supplier);
            setRemoteItems(res.items);
            setSelected(toKeySet(res.items.filter((row) => row.selected).map((row) => row.externalId)));
            setPage(1);
            onMessage({
                type: 'ok',
                text: `Catálogo de ${supplier}: ${esCount(res.items.length, 'ficha')} · ${esCount(res.allowlistCount, 'marcada')}`,
            });
        } catch (e: unknown) {
            onMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
        }
    };

    const onSave = async () => {
        try {
            const items = [...selected].map((externalId) => {
                const meta = metaById.get(externalId);
                return {
                    externalId,
                    nombre: meta?.nombre || externalId,
                    sourceUrl: meta?.sourceUrl || undefined,
                };
            });
            const res = await saveMut.mutateAsync({ supplier, items });
            onMessage({
                type: 'ok',
                text: `Selección de ${supplier}: ${esCount(res.saved ?? items.length, 'producto')}. El próximo sync solo refresca estos.`,
            });
        } catch (e: unknown) {
            onMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
        }
    };

    const columns: ResourceIndexColumn<CatalogRemoteItem>[] = [
        {
            id: 'nombre',
            header: 'Producto',
            cell: (row) => (
                <span>
                    <span className="block font-medium">{row.nombre}</span>
                    <span className="block font-mono text-xs text-muted-foreground">{row.externalId}</span>
                </span>
            ),
        },
        {
            id: 'imported',
            header: 'En tienda',
            cell: (row) => (
                <StatusBadge status={row.imported ? 'published' : 'pending'} label={row.imported ? 'Importado' : 'Sin importar'} />
            ),
        },
    ];

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                Marca qué fichas de {supplier} se venden en AiPeces. El sync solo actualiza precio, stock e imágenes de las marcadas.
            </p>
            <ResourceIndex
                actions={(
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" disabled={remoteMut.isPending} onClick={() => { void onLoadRemote(); }}>
                            {remoteMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                            Cargar catálogo del proveedor
                        </Button>
                        <Button type="button" size="sm" disabled={saveMut.isPending} onClick={() => { void onSave(); }}>
                            Guardar selección ({selected.size})
                        </Button>
                    </div>
                )}
                views={[
                    { id: 'all', label: 'Todos', count: rows.length },
                    { id: 'selected', label: 'Marcados', count: rows.filter((row) => selected.has(row.externalId)).length },
                    { id: 'imported', label: 'Importados', count: rows.filter((row) => row.imported).length },
                    { id: 'pending', label: 'Marcados sin importar', count: rows.filter((row) => selected.has(row.externalId) && !row.imported).length },
                ]}
                view={view}
                onViewChange={(id) => { setView(id as AllowView); setPage(1); }}
                search={search}
                onSearchChange={(value) => { setSearch(value); setPage(1); }}
                searchPlaceholder="Nombre, referencia o URL…"
                chips={[]}
                onRemoveChip={() => undefined}
                columns={columns}
                rows={pageRows}
                rowId={(row) => row.externalId}
                sort={null}
                sortDir="asc"
                onSort={() => undefined}
                selectedIds={selectedIds}
                onToggleRow={(id) => setSelected((prev) => new Set(toggleSelectedId([...prev], id)))}
                onTogglePage={(checked) => setSelected((prev) => new Set(selectPageIds([...prev], pageRows.map((row) => row.externalId), checked)))}
                onSelectView={() => setSelected((prev) => {
                    const next = new Set(prev);
                    for (const row of filtered) next.add(row.externalId);
                    return next;
                })}
                page={page}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onPageChange={setPage}
                isLoading={savedQ.isLoading}
                error={savedQ.isError ? 'No se ha podido cargar la selección.' : null}
                emptyTitle={remoteItems ? 'Ningún producto con este filtro' : 'Todavía no hay productos marcados'}
                emptyDescription={remoteItems ? undefined : 'Carga el catálogo del proveedor para elegir fichas.'}
            />
        </div>
    );
}
